<template>
    <div class="lot-view-container" :style="{ height: height }">
        <!-- Location selector overlay -->
        <div class="lot-location-selector" @click.stop>
            <v-select v-model="selectedLocationId"
                      :items="locationItems"
                      item-title="name"
                      item-value="id"
                      density="compact"
                      variant="solo"
                      hide-details
                      bg-color="white"
                      style="min-width: 220px; max-width: 300px;"
                      placeholder="Select location"
                      :loading="loadingLocationData"
            />
        </div>

        <!-- Zone summary chips -->
        <div v-if="zoneSummary.length" class="lot-zone-summary" @click.stop>
            <v-chip v-for="z in zoneSummary" :key="z.id"
                    :color="z.color"
                    variant="flat"
                    size="small"
                    class="me-1 mb-1">
                {{ z.name }}: {{ z.count }} vehicle{{ z.count !== 1 ? 's' : '' }}
            </v-chip>
        </div>

        <!-- Color legend -->
        <div class="lot-legend" @click.stop>
            <div class="lot-legend-inner">
                <span v-for="item in legendItems" :key="item.label" class="lot-legend-item">
                    <span class="lot-legend-dot" :style="{ background: item.color }"></span>
                    <span class="lot-legend-label">{{ item.label }}</span>
                </span>
            </div>
        </div>

        <!-- Empty state: no locations -->
        <div v-if="!locationItems.length" class="lot-empty-state">
            <v-icon size="64" color="grey-lighten-1">mdi-map-marker-off</v-icon>
            <div class="text-grey mt-2">No company locations configured</div>
        </div>

        <!-- Map -->
        <l-map v-else ref="mapRef"
               :zoom="zoom"
               :center="mapCenter"
               :use-global-leaflet="false"
               :options="{ zoomControl: true, attributionControl: false }"
               @ready="onMapReady">
            <!-- Satellite base layer -->
            <l-tile-layer
                :url="$mapTiles.satellite"
                :options="{ maxZoom: 19 }"
            />
            <!-- Labels overlay -->
            <l-tile-layer
                :url="$mapTiles.satelliteLabels"
                :options="{ maxZoom: 19, pane: 'overlayPane' }"
            />
        </l-map>
    </div>
</template>

<script setup>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {send} from "@/composables/apiClient";
import {computed, inject, onBeforeUnmount, onMounted, ref, watch, nextTick} from "vue";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {getStoppedLabel} from "@/composables/fleetStatus";

const props = defineProps(['devices', 'companyLocations', 'height', 'selectedDevice']);
const emits = defineEmits(['selectedMarker']);

const mapRef = ref(null);
const leafletMap = ref(null);
const zoom = ref(17);
const mapCenter = ref([33.749, -84.388]);
const selectedLocationId = ref(null);
const locationData = ref(null);
const loadingLocationData = ref(false);
const lastMqttUpdate = inject('lastMqttUpdate', ref(null));

// Leaflet layer groups
const zonePolygonsGroup = ref(null);
const spotPolygonsGroup = ref(null);
const locationPolygonGroup = ref(null);
const deviceMarkersGroup = ref(null);
const zoneLabelGroup = ref(null);

// Parked duration tracking
const parkedSinceMap = ref(new Map()); // device.id → timestamp when parking started
let colorRefreshTimer = null;

// ─── Constants ───
const ZONE_COLORS = {
    0: {fill: '#4CAF50', label: 'Open Parking'},      // OPEN_PARKING
    1: {fill: '#2196F3', label: 'Covered Parking'},    // COVERED_PARKING
    2: {fill: '#795548', label: 'Building'},            // BUILDING
    3: {fill: '#FF9800', label: 'Work Site'},           // WORK_SITE
    4: {fill: '#9C27B0', label: 'Multi-Level Parking'}, // MULTI_LEVEL_PARKING
    5: {fill: '#00BCD4', label: 'Single-Level Indoor'}, // SINGLE_LEVEL_INDOOR
    6: {fill: '#673AB7', label: 'Multi-Level Indoor'},  // MULTI_LEVEL_INDOOR
};

const DURATION_COLORS = [
    {max: 0, color: '#4CAF50', label: 'Moving'},
    {max: 60, color: '#4CAF50', label: '< 1 hour'},
    {max: 240, color: '#FFC107', label: '1-4 hours'},
    {max: 1440, color: '#FF9800', label: '4-24 hours'},
    {max: Infinity, color: '#F44336', label: '> 24 hours'},
];

const legendItems = DURATION_COLORS.map(d => ({color: d.color, label: d.label}));

// ─── Computed ───
const locationItems = computed(() => {
    return (props.companyLocations || []).filter(loc =>
        loc.polygon_coordinates && loc.polygon_coordinates.length > 0
    );
});

const devicesInLocation = computed(() => {
    if (!selectedLocationId.value || !props.devices) return [];
    const locId = selectedLocationId.value;

    // First: devices with matching location_id
    const byId = props.devices.filter(d => d.location_id === locId);
    const byIdSet = new Set(byId.map(d => d.id));

    // Then: devices without location_id that fall inside the location polygon
    const locationCoords = getLocationPolygonCoords();
    if (locationCoords.length > 0) {
        const polygon = L.polygon(locationCoords);
        const byPoly = props.devices.filter(d =>
            !byIdSet.has(d.id) && d.lat && d.lng &&
            polygon.getBounds().contains(L.latLng(d.lat, d.lng))
        );
        return [...byId, ...byPoly];
    }
    return byId;
});

const zoneSummary = computed(() => {
    if (!locationData.value || !locationData.value.zones) return [];
    const devs = devicesInLocation.value;

    return locationData.value.zones.map(zone => {
        const coords = (zone.polygon_coordinates || []).map(c => [c.latitude, c.longitude]);
        let count = 0;
        if (coords.length >= 3) {
            const poly = L.polygon(coords);
            count = devs.filter(d => d.lat && d.lng && poly.getBounds().contains(L.latLng(d.lat, d.lng))).length;
        }
        const zoneType = zone.type ?? 0;
        return {
            id: zone.id,
            name: zone.name,
            count,
            color: ZONE_COLORS[zoneType]?.fill || '#607D8B',
        };
    });
});

// ─── Methods ───
function getLocationPolygonCoords() {
    if (!locationData.value || !locationData.value.polygon_coordinates) return [];
    return locationData.value.polygon_coordinates.map(c => [c.latitude, c.longitude]);
}

function getDurationColor(device) {
    // Moving/idling devices are green
    if (device.motion_state === 1 && Math.max(device.gps_speed_raw ?? device.gps_speed ?? 0, device.obd_present ? (device.obd_speed ?? 0) : 0) > 2) return DURATION_COLORS[0].color;

    // Calculate parked duration
    const parkedSince = parkedSinceMap.value.get(device.id);
    let minutesParked = 0;

    if (parkedSince) {
        minutesParked = (Date.now() - parkedSince) / 60000;
    } else {
        // Fallback: use last_message_received
        let ts = String(device.last_message_received || '');
        if (ts && !ts.endsWith('Z') && !ts.includes('+')) {
            ts = ts.replace(' ', 'T') + 'Z';
        }
        const lastMsg = new Date(ts);
        if (!isNaN(lastMsg.getTime())) {
            minutesParked = (Date.now() - lastMsg.getTime()) / 60000;
        }
    }

    for (const tier of DURATION_COLORS) {
        if (tier.max === 0) continue; // skip "Moving" tier
        if (minutesParked <= tier.max) return tier.color;
    }
    return DURATION_COLORS[DURATION_COLORS.length - 1].color;
}

function getDeviceInitial(device) {
    const label = device.asset_label || device.name || device.slno || '?';
    return label.charAt(0).toUpperCase();
}

async function fetchLocationData(locationId) {
    if (!locationId) return;
    loadingLocationData.value = true;
    try {
        const response = await send("get", `companies/locations/${locationId}/lot-view`);
        locationData.value = response.data.data;
    } catch (err) {
        console.error('Failed to fetch lot view data:', err);
        locationData.value = null;
    } finally {
        loadingLocationData.value = false;
    }
}

let mapIsReady = false;

function onMapReady() {
    leafletMap.value = mapRef.value?.leafletObject;
    if (!leafletMap.value) return;

    zonePolygonsGroup.value = new L.FeatureGroup().addTo(leafletMap.value);
    spotPolygonsGroup.value = new L.FeatureGroup().addTo(leafletMap.value);
    locationPolygonGroup.value = new L.FeatureGroup().addTo(leafletMap.value);
    deviceMarkersGroup.value = new L.FeatureGroup().addTo(leafletMap.value);
    zoneLabelGroup.value = new L.FeatureGroup().addTo(leafletMap.value);
    mapIsReady = true;

    // If location data already fetched (auto-select fired before map ready), draw now
    tryRenderLocation();
}

function tryRenderLocation() {
    if (!mapIsReady || !leafletMap.value || !locationData.value) return;
    clearAll();
    drawLocationBoundary();
    drawZones();
    drawSpots();
    drawDeviceMarkers();
    initParkedSince();
    // Delay fitBounds to let the map container settle after view switch
    setTimeout(() => {
        if (!leafletMap.value) return;
        const coords = getLocationPolygonCoords();
        if (coords.length < 3) return;
        leafletMap.value.invalidateSize();
        leafletMap.value.fitBounds(L.latLngBounds(coords), {padding: [30, 30], maxZoom: 19});
    }, 200);
}

function clearAll() {
    zonePolygonsGroup.value?.clearLayers();
    spotPolygonsGroup.value?.clearLayers();
    locationPolygonGroup.value?.clearLayers();
    deviceMarkersGroup.value?.clearLayers();
    zoneLabelGroup.value?.clearLayers();
}

function drawLocationBoundary() {
    if (!locationPolygonGroup.value) return;
    locationPolygonGroup.value.clearLayers();

    const coords = getLocationPolygonCoords();
    if (coords.length < 3) return;

    L.polygon(coords, {
        color: '#FFFFFF',
        weight: 2,
        fillOpacity: 0.05,
        dashArray: '8, 4',
    }).addTo(locationPolygonGroup.value);
}

function drawZones() {
    if (!zonePolygonsGroup.value || !zoneLabelGroup.value) return;
    zonePolygonsGroup.value.clearLayers();
    zoneLabelGroup.value.clearLayers();

    const zones = locationData.value?.zones || [];
    zones.forEach(zone => {
        const coords = (zone.polygon_coordinates || []).map(c => [c.latitude, c.longitude]);
        if (coords.length < 3) return;

        const zoneType = zone.type ?? 0;
        const fillColor = ZONE_COLORS[zoneType]?.fill || '#607D8B';

        L.polygon(coords, {
            color: fillColor,
            weight: 2,
            fillColor: fillColor,
            fillOpacity: 0.25,
        }).addTo(zonePolygonsGroup.value);

        // Zone label at centroid
        const centroid = L.polygon(coords).getBounds().getCenter();
        const labelIcon = L.divIcon({
            className: 'zone-label-icon',
            html: `<div class="zone-label-chip" style="background:${fillColor}">${zone.name}</div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
        });
        L.marker(centroid, {icon: labelIcon, interactive: false}).addTo(zoneLabelGroup.value);
    });
}

function drawSpots() {
    if (!spotPolygonsGroup.value) return;
    spotPolygonsGroup.value.clearLayers();

    const zones = locationData.value?.zones || [];
    zones.forEach(zone => {
        (zone.spots || []).forEach(spot => {
            const coords = (spot.polygon_coordinates || []).map(c => [c.latitude, c.longitude]);
            if (coords.length < 3) return;

            L.polygon(coords, {
                color: '#9C27B0',
                weight: 1,
                fillOpacity: 0.08,
                dashArray: '4, 4',
            }).addTo(spotPolygonsGroup.value);
        });
    });
}

function drawDeviceMarkers() {
    if (!deviceMarkersGroup.value) return;
    deviceMarkersGroup.value.clearLayers();

    const devs = devicesInLocation.value;
    devs.forEach(device => {
        if (!device.lat || !device.lng) return;

        const color = getDurationColor(device);
        const initial = getDeviceInitial(device);
        const isSelected = props.selectedDevice && props.selectedDevice.id === device.id;

        const markerIcon = L.divIcon({
            className: 'lot-device-marker',
            html: `<div class="lot-marker-circle${isSelected ? ' lot-marker-selected' : ''}" style="background:${color};border-color:${color}">${initial}</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
        });

        const marker = L.marker([device.lat, device.lng], {icon: markerIcon, deviceId: device.id});

        // Popup content
        const speed = Math.max(device.gps_speed_raw ?? device.gps_speed ?? 0, device.obd_present ? (device.obd_speed ?? 0) : 0);
        const motionLabel = device.motion_state === 1 ? (speed > 2 ? 'In Motion' : 'Idling') : getStoppedLabel(device);
        const parkedTime = getParkedTimeLabel(device);
        const popupHtml = `
            <div style="min-width:160px;font-size:13px;">
                <div style="font-weight:600;margin-bottom:4px;">${device.asset_label || device.name || device.slno}</div>
                <div style="color:${color};font-weight:500;">${motionLabel}</div>
                ${parkedTime ? `<div style="color:#666;font-size:12px;">${parkedTime}</div>` : ''}
                ${device.driver_first_name ? `<div style="font-size:12px;color:#666;margin-top:2px;">Driver: ${device.driver_first_name} ${device.driver_last_name || ''}</div>` : ''}
            </div>
        `;
        marker.bindPopup(popupHtml, {offset: [0, -8]});
        marker.on('click', () => emits('selectedMarker', device));
        marker.addTo(deviceMarkersGroup.value);
    });
}

function getParkedTimeLabel(device) {
    if (device.motion_state === 1) return '';

    const parkedSince = parkedSinceMap.value.get(device.id);
    let minutes = 0;

    if (parkedSince) {
        minutes = (Date.now() - parkedSince) / 60000;
    } else {
        let ts = String(device.last_message_received || '');
        if (ts && !ts.endsWith('Z') && !ts.includes('+')) {
            ts = ts.replace(' ', 'T') + 'Z';
        }
        const lastMsg = new Date(ts);
        if (!isNaN(lastMsg.getTime())) {
            minutes = (Date.now() - lastMsg.getTime()) / 60000;
        }
    }

    const label = getStoppedLabel(device);
    if (minutes < 1) return `Just ${label.toLowerCase()}`;
    if (minutes < 60) return `${label} ${Math.floor(minutes)}m ago`;
    if (minutes < 1440) return `${label} ${Math.floor(minutes / 60)}h ${Math.floor(minutes % 60)}m ago`;
    const days = Math.floor(minutes / 1440);
    return `${label} ${days}d ${Math.floor((minutes % 1440) / 60)}h ago`;
}

function initParkedSince() {
    // Initialize parked-since timestamps from current device state
    const devs = devicesInLocation.value;
    devs.forEach(device => {
        if (device.motion_state !== 1 && !parkedSinceMap.value.has(device.id)) {
            let ts = String(device.last_message_received || '');
            if (ts && !ts.endsWith('Z') && !ts.includes('+')) {
                ts = ts.replace(' ', 'T') + 'Z';
            }
            const d = new Date(ts);
            if (!isNaN(d.getTime())) {
                parkedSinceMap.value.set(device.id, d.getTime());
            }
        }
    });
}

function refreshMarkerColors() {
    // Redraw markers to reflect updated duration colors
    drawDeviceMarkers();
}

// ─── Watchers ───
watch(selectedLocationId, async (newId) => {
    if (!newId) {
        locationData.value = null;
        clearAll();
        return;
    }
    await fetchLocationData(newId);
    await nextTick();
    tryRenderLocation();
});

// Auto-select first location
watch(locationItems, (items) => {
    if (items.length && !selectedLocationId.value) {
        selectedLocationId.value = items[0].id;
    }
}, {immediate: true});

// Re-draw markers when devices change (MQTT position updates mutate the array)
watch(() => props.devices, () => {
    if (leafletMap.value && locationData.value) {
        drawDeviceMarkers();
    }
}, {deep: true});

// MQTT motion state tracking
watch(lastMqttUpdate, (update) => {
    if (!update) return;
    const slno = update.slno;
    const payload = update.data;

    // Find device in our filtered list
    const device = devicesInLocation.value.find(d => d.slno === slno);
    if (!device) return;

    // Update parkedSince on motion transitions
    if ([2, 3, 4].includes(payload.message_type)) {
        if (payload.message_type === 4) {
            // MOTION_ENDED → mark as parked now
            parkedSinceMap.value.set(device.id, Date.now());
        } else {
            // MOTION_STARTED → remove from parked tracking
            parkedSinceMap.value.delete(device.id);
        }
    }

    // Redraw markers for color update
    if (leafletMap.value) {
        drawDeviceMarkers();
    }
});

// ─── Lifecycle ───
onMounted(() => {
    colorRefreshTimer = setInterval(refreshMarkerColors, 60000);
});

onBeforeUnmount(() => {
    if (colorRefreshTimer) {
        clearInterval(colorRefreshTimer);
        colorRefreshTimer = null;
    }
});
</script>

<style scoped>
.lot-view-container {
    position: relative;
    width: 100%;
}

.lot-location-selector {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1001;
}

.lot-zone-summary {
    position: absolute;
    bottom: 10px;
    left: 10px;
    z-index: 1001;
    display: flex;
    flex-wrap: wrap;
    max-width: 60%;
}

.lot-legend {
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 1001;
}

.lot-legend-inner {
    background: rgba(0, 0, 0, 0.75);
    border-radius: 6px;
    padding: 6px 10px;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.lot-legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.lot-legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
}

.lot-legend-label {
    color: #fff;
    font-size: 11px;
    white-space: nowrap;
}

.lot-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    position: absolute;
    inset: 0;
    z-index: 1;
    background: #f5f5f5;
}
</style>

<style>
/* Global styles for lot view (Leaflet DivIcons render outside scoped) */
.zone-label-icon {
    background: none !important;
    border: none !important;
}

.zone-label-chip {
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
    transform: translate(-50%, -50%);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}

.lot-device-marker {
    background: none !important;
    border: none !important;
}

.lot-marker-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s ease;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}

.lot-marker-circle:hover {
    transform: scale(1.3);
}

.lot-marker-selected {
    box-shadow: 0 0 0 3px #fff, 0 0 0 5px #2196F3;
}
</style>
